// @ts-nocheck
import * as React from 'react';
import Icon from '../../components/common/Icon';
import { PageHero, Section, SendBtn } from '../shared/InternalShared';

// helpdesk.jsx — AI Parental Helpdesk. Accent: amber.
// "Every parenting question. A research-backed answer."

import { useState } from "react";

const HD_QA = [
  {
    q: "How do I help Aarav focus on homework?",
    a: "Start with a fixed 25-minute window at the same time daily — predictability lowers resistance. Remove the phone from the room, not just the table. Praise the sitting-down, not the marks, for the first two weeks.",
    basis: "Daniel Goleman · Emotional Intelligence",
    fmt: "2-min audio",
  },
  {
    q: "He gets anxious before tests. What helps?",
    a: "Anxiety eases with rehearsal, not reassurance. Do one timed mini-practice so the exam feels familiar, then a calm bedtime routine the night before. Avoid 'do your best' pressure on the morning of.",
    basis: "Manodarpan · Govt. of India",
    fmt: "3-min video",
  },
  {
    q: "How much screen time is healthy at age 8?",
    a: "Aim for under an hour of recreational screen on school days, with screens off 60 minutes before sleep. Co-watch when you can — shared screens build conversation, solo screens build attachment.",
    basis: "John Bowlby · Attachment Theory",
    fmt: "90-sec audio",
  },
  {
    q: "How do I work better with his teachers?",
    a: "Share one specific observation from home each month, not a list of worries. Partnership works when both sides bring data — the 360° report gives you exactly that to discuss.",
    basis: "Dr. Joyce Epstein · Parent–School",
    fmt: "2-min audio",
  },
];

const HD_PILLARS = [
  { id: "parenting", name: "Parenting Guidance",        icon: "parent",   topics: ["Behaviour", "Motivation", "Academic help", "Discipline"], out: "Stability · steady growth" },
  { id: "health",    name: "Health & Nutrition",        icon: "shield",   topics: ["Nutrition", "Physical health", "Immunity", "Energy"],      out: "Vitality · balance" },
  { id: "mental",    name: "Mental & Emotional Support", icon: "sparkles", topics: ["Anxiety", "Emotional regulation", "Stress"],               out: "Resilience · confidence" },
  { id: "special",   name: "Special Needs Support",      icon: "student",  topics: ["ADHD / Dyslexia", "Learning differences", "Inclusive support"], out: "Inclusion · empowerment" },
];

export default function PageHelpdesk({ student, accent, sendToParent }) {
  const [qi, setQi] = useState(0);
  const qa = HD_QA[qi];

  const heroRight = (
    <div className="ip-rise" style={{ display: "grid", gap: 12 }}>
      <div className="ip-ask">
        <Icon name="sparkles" size={18} style={{ color: "var(--accent)" }} />
        <input value={qa.q} readOnly aria-label="Ask the helpdesk" />
        <button className="ip-btn ip-btn--accent ip-btn--sm">Ask</button>
      </div>
      <div className="ip-answer" key={qi}>
        <div className="ip-answer__head">
          <div className="ip-iconbox" style={{ width: 32, height: 32, borderRadius: 9 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="7 4 20 12 7 20 7 4"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{qa.fmt} · in {student.language}</div>
            <div className="ip-answer__q">Personalised answer</div>
          </div>
        </div>
        <div className="ip-answer__body">
          <p className="ip-answer__p">{qa.a}</p>
          <div className="ip-answer__basis"><Icon name="shield" size={14} /><span>Research-backed · <strong>{qa.basis}</strong></span></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ip" style={{ "--accent": accent }}>
      <PageHero
        eyebrow="AI Parental Helpdesk"
        eyebrowBadge="Premium"
        title={<>Every parenting question.<br/>A <span className="ip-accent">research-backed</span> answer.</>}
        lead="Parents ask anything — about behaviour, health, emotions or learning differences — and receive a short, personalised audio or video answer grounded in real science. Available 24×7."
        actions={<>
          <SendBtn onClick={() => sendToParent("AI Helpdesk answer")} label="Send this answer" />
        </>}
        trust={{ label: "Aligned with", items: [
          { label: "Manodarpan" },
          { label: "Ministry of Education" },
        ] }}
        right={heroRight}
      />

      <div className="ip-premium-band">
        <div className="ip-premium-band__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M4.5 19h15l1.2-10.2-4.2 2.7L12 5.2l-4.5 6.3-4.2-2.7L4.5 19z" />
            <path d="M4 21h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="ip-premium-band__copy">
          <h2>Why Is This Premium ?</h2>
          <p>Powered by premium AI tools to provide research-backed guidance and personalised answers for your child.</p>
        </div>
        <div className="ip-premium-band__price">
          <span>Market value</span>
          <strong className="ip-premium-band__strike">₹5000</strong>
          <em>Available at</em>
          <strong>₹499</strong>
        </div>
      </div>

      <Section eyebrow="Try it" title="Questions parents really ask" hint="Tap a question — the answer updates instantly, in the parent's language.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {HD_QA.map((item, i) => (
            <button key={i} className={"ip-chip" + (i === qi ? " is-on" : "")} onClick={() => setQi(i)} style={{ height: "auto", padding: "9px 14px", whiteSpace: "normal", textAlign: "left", maxWidth: 320 }}>
              {item.q}
            </button>
          ))}
        </div>
      </Section>

      <Section eyebrow="Four pillars of guidance" title="Support across every part of growing up" style={{ paddingTop: 4 }}>
        <div className="ip-grid ip-grid--4">
          {HD_PILLARS.map((p) => (
            <div key={p.id} className="ip-card ip-card--hover" style={{ padding: 18 }}>
              <div className="ip-iconbox"><Icon name={p.icon} size={20} /></div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--fg)", marginTop: 13, letterSpacing: "-0.01em", lineHeight: 1.25 }}>{p.name}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 11 }}>
                {p.topics.map((t) => (
                  <span key={t} style={{ fontSize: 12, color: "var(--fg-secondary)", display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 4, height: 4, borderRadius: 999, background: "var(--accent)", flexShrink: 0 }} />{t}
                  </span>
                ))}
              </div>
              <hr className="ip-hr" style={{ margin: "13px 0 11px" }} />
              <span className="ip-tag">{p.out}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="The minds behind every answer" title="Grounded in real science" style={{ paddingTop: 4 }}>
        <div className="ip-grid ip-grid--3">
          {[
            { i: "DG", n: "Daniel Goleman", w: "Emotional Intelligence", bg: "linear-gradient(135deg,#bbf7d0,#16a34a)" },
            { i: "JB", n: "John Bowlby", w: "Attachment Theory", bg: "linear-gradient(135deg,#ddd6fe,#7c3aed)" },
            { i: "JE", n: "Dr. Joyce Epstein", w: "Parent–School Partnership", bg: "linear-gradient(135deg,#fed7aa,#ea580c)" },
          ].map((L) => (
            <div key={L.i} className="ip-card" style={{ padding: 16, display: "flex", alignItems: "center", gap: 13 }}>
              <div style={{ width: 42, height: 42, borderRadius: 999, background: L.bg, color: "#1f1300", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{L.i}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg)" }}>{L.n}</div>
                <div style={{ fontSize: 12, color: "var(--fg-tertiary)", marginTop: 2 }}>{L.w}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="ip-band" style={{ "--accent": accent, marginBottom: 36 }}>
        <span className="ip-band__eyebrow">The mission</span>
        <div className="ip-band__title">Stable home. Strong focus.</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.72)", marginTop: 12, maxWidth: 600, position: "relative", lineHeight: 1.6 }}>
          When parents feel equipped, children feel secure — and a secure child has the steady focus that competitive exams reward.
        </div>
      </div>
    </div>
  );
}




